import { useState, type FormEvent } from "react";
import { Link, useLocation, useParams } from "react-router";
import { adminApi, ApiError, type AdminProviderDetail, type UpdateAdminProviderInput } from "../../../lib/api";
import { consoleApi } from "../../../lib/admin-console-api";
import { useAuth } from "../../../lib/auth";
import { useAppPreferences } from "../../../lib/preferences";
import { useConfirm } from "../../../components/ConfirmDialog";
import { AssistOwnerControl } from "../../admin-workspace/AssistanceControls";
import { ActivityTimeline, NotesPanel } from "../activity";
import { canResendProviderOwnerSetup, ProviderDecision, type ProviderDecisionInput } from "../ProviderDecision";
import { currentMonth, errorMessage, Facts, LoadState, monthBounds, PageHeader, Section, StatusChip, useFormatters, useLoad, useNotices } from "../ui";

const MEMBERSHIP_LABELS: Record<string, string> = { owner: "Owner", manager: "Manager", front_desk: "Front desk" };

export function ProviderRecordPage() {
  const { providerId = "" } = useParams();
  const { t } = useAppPreferences();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const location = useLocation();
  const { dateOnly, dateTime, money } = useFormatters();
  const { confirm, dialog } = useConfirm();
  const { banners, setError, setNotice } = useNotices();
  const [initialNotice] = useState<string | null>((location.state as { notice?: string } | null)?.notice ?? null);
  const provider = useLoad(() => adminApi.getProvider(providerId), [providerId]);
  const settlement = useLoad(
    () => (isAdmin ? consoleApi.providerSettlement(providerId, monthBounds(currentMonth())) : Promise.resolve(null)),
    [providerId, isAdmin],
  );
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(false);
  const detail = provider.data;

  const decide = async (input: ProviderDecisionInput) => {
    if (!detail) return;
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      await adminApi.updateProviderStatus(detail.id, {
        approval_status: input.status,
        review_note: input.note.trim() || null,
        reason_code: input.status === "rejected" || input.status === "suspended" ? input.reason || null : null,
      });
      setNote("");
      setNotice(t("Status updated."));
      await provider.reload();
    } catch (error) {
      setError(errorMessage(error, t("The decision was not saved. Your note is kept; try again.")));
    } finally {
      setBusy(false);
    }
  };

  const resendSetup = async (target: AdminProviderDetail) => {
    const confirmed = await confirm({
      title: target.owner_setup_pending ? t("Invite the owner to their workspace?") : t("Resend the owner's setup link?"),
      description: target.owner_setup_pending
        ? t("Send {email} a fresh 7-day password setup link. Make sure the venues are ready first.", { email: target.contact_email })
        : t("This signs {email} out, locks the owner account and emails a new 7-day setup link.", { email: target.contact_email }),
      confirmButtonLabel: target.owner_setup_pending ? t("Send setup link") : t("Resend setup link"),
      cancelButtonLabel: t("Cancel"),
    });
    if (!confirmed) return;
    setBusy(true);
    setError(null);
    try {
      await adminApi.resendProviderOwnerSetup(target.id);
      setNotice(t("A password setup link was sent to {email}.", { email: target.contact_email }));
      await provider.reload();
    } catch (error) {
      setError(
        error instanceof ApiError && error.code === "setup_email_delivery_failed"
          ? t("The setup email was not delivered. The owner stays locked; you can safely retry.")
          : errorMessage(error, t("The setup link was not sent.")),
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="console-page">
      {dialog}
      <LoadState error={provider.error} isEmpty={!detail} loading={provider.loading} onRetry={() => void provider.reload()}>
        {detail ? (
          <>
            <PageHeader
              actions={
                <>
                  {isAdmin ? (
                    <button className="ops-button" data-size="sm" data-variant="secondary" onClick={() => setEditing((open) => !open)} type="button">
                      {editing ? t("Close editor") : t("Edit profile")}
                    </button>
                  ) : null}
                  {canResendProviderOwnerSetup(user?.role) ? (
                    <button className="ops-button" data-size="sm" data-variant="warning" disabled={busy} onClick={() => void resendSetup(detail)} type="button">
                      {detail.owner_setup_pending ? t("Send setup link") : t("Resend setup link")}
                    </button>
                  ) : null}
                </>
              }
              description={<><StatusChip status={detail.approval_status} /> <span className="console-muted">{detail.city}</span></>}
              kicker={<Link className="console-link" to="/admin/providers">{t("Providers")}</Link>}
              title={detail.business_name}
            />
            {initialNotice ? <div className="ops-banner" data-tone="success" role="status">{initialNotice}</div> : null}
            {banners}

            {editing && isAdmin ? (
              <ProviderEditForm
                detail={detail}
                onSaved={async () => {
                  setEditing(false);
                  setNotice(t("Profile saved."));
                  await provider.reload();
                }}
              />
            ) : null}

            <div className="console-record-grid">
              <div className="console-record-main">
                <Section title={t("Summary")}>
                  <Facts
                    items={[
                      [t("Owner"), detail.contact_name],
                      [t("Owner email"), detail.contact_email],
                      [t("Phone"), detail.contact_phone],
                      [t("Owner setup"), detail.owner_setup_pending ? t("Waiting for password setup") : t("Complete")],
                      [t("Joined"), dateOnly(detail.created_at)],
                      [t("Last decision"), detail.reviewed_at ? t("{when} by {who}", { when: dateTime(detail.reviewed_at), who: detail.reviewed_by_name ?? "—" }) : null],
                      [t("Venues"), t("{active} live of {total}", { active: detail.active_venues, total: detail.venues.length })],
                      [t("Bookings"), t("{total} total · {recent} in the last 7 days", { total: detail.booking_count, recent: detail.bookings_last_7_days })],
                    ]}
                  />
                  {detail.application_note ? <p className="console-quote">{t("Application note: {note}", { note: detail.application_note })}</p> : null}
                  {detail.review_note ? <p className="console-quote">{t("Note to the provider: {note}", { note: detail.review_note })}</p> : null}
                </Section>

                <Section title={t("Decision")}>
                  <ProviderDecision busy={busy} isAdmin={isAdmin} note={note} onDecide={(input) => void decide(input)} onNoteChange={setNote} provider={detail} />
                  {detail.approval_status === "approved" && !isAdmin ? <p className="console-muted">{t("Only admins can suspend a provider.")}</p> : null}
                </Section>

                <Section title={t("Venues")}>
                  {detail.venues.length === 0 ? (
                    <p className="console-muted">{t("No venues yet.")}</p>
                  ) : (
                    <ul className="console-list">
                      {detail.venues.map((venue) => (
                        <li key={venue.id}>
                          <Link className="console-list-row" to={`/admin/venues/${venue.id}`}>
                            <span className="min-w-0 flex-1">
                              <span className="console-list-title">{venue.name}</span>
                              <span className="console-muted">
                                {venue.city} ·{" "}
                                {Object.entries(venue.setup_checks ?? {}).filter(([, done]) => !done).length === 0
                                  ? t("Setup complete")
                                  : t("Missing: {items}", {
                                      items: Object.entries(venue.setup_checks ?? {})
                                        .filter(([, done]) => !done)
                                        .map(([label]) => t(label))
                                        .join(", "),
                                    })}
                              </span>
                            </span>
                            <StatusChip status={venue.approval_status} />
                          </Link>
                        </li>
                      ))}
                    </ul>
                  )}
                </Section>

                <Section title={t("Team")}>
                  <ul className="console-list">
                    {detail.members.map((member) => (
                      <li key={member.user_id}>
                        <Link className="console-list-row" to={`/admin/accounts/${member.user_id}`}>
                          <span className="min-w-0 flex-1">
                            <span className="console-list-title">{member.display_name ?? member.email}</span>
                            <span className="console-muted console-truncate">{member.email}</span>
                          </span>
                          <span className="ops-chip">{t(MEMBERSHIP_LABELS[member.membership_role ?? ""] ?? member.role)}</span>
                          {!member.is_active ? <StatusChip status="inactive" /> : member.setup_pending ? <span className="ops-chip" data-tone="warning">{t("Setup pending")}</span> : null}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </Section>

                <ActivityTimeline filters={{ provider_id: detail.id }} />
              </div>

              <div className="console-record-side">
                {isAdmin ? (
                  <Section
                    actions={<Link className="console-link" to={`/admin/settlements/${detail.id}`}>{t("Open settlement")}</Link>}
                    title={t("Settlement")}
                  >
                    <LoadState error={settlement.error} isEmpty={!settlement.data} loading={settlement.loading} onRetry={() => void settlement.reload()}>
                      {settlement.data ? (
                        <>
                          <Facts
                            items={[
                              [t("Balance owed to gaamed"), money(settlement.data.balance.balance_amount)],
                              [t("Overdue"), money(settlement.data.balance.overdue_amount)],
                              [t("This month so far"), money(settlement.data.preview.commission_amount)],
                              [t("Last payment"), settlement.data.balance.last_payment_on ? dateOnly(settlement.data.balance.last_payment_on) : t("None yet")],
                            ]}
                          />
                          {settlement.data.venues_without_rules.length > 0 ? (
                            <p className="console-warning">
                              {t("No commission terms at: {venues}", { venues: settlement.data.venues_without_rules.join(", ") })}
                            </p>
                          ) : null}
                        </>
                      ) : null}
                    </LoadState>
                  </Section>
                ) : null}

                {isAdmin ? (
                  <Section description={t("Work in this owner's real workspace for up to 60 minutes. Every change is recorded under your name.")} title={t("Assist the owner")}>
                    <AssistOwnerControl approved={detail.approval_status === "approved"} key={detail.id} providerId={detail.id} />
                  </Section>
                ) : null}

                <NotesPanel entityId={detail.id} entityType="provider" />
              </div>
            </div>
          </>
        ) : null}
      </LoadState>
    </div>
  );
}

function ProviderEditForm({ detail, onSaved }: { detail: AdminProviderDetail; onSaved: () => Promise<void> }) {
  const { t } = useAppPreferences();
  const [form, setForm] = useState<UpdateAdminProviderInput>({
    business_name: detail.business_name,
    contact_name: detail.contact_name ?? "",
    city: detail.city,
    contact_phone: detail.contact_phone ?? "",
    application_note: detail.application_note ?? "",
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setSaving(true);
    setError(null);
    try {
      await adminApi.updateProvider(detail.id, {
        ...form,
        contact_phone: form.contact_phone?.trim() || null,
        application_note: form.application_note?.trim() || null,
      });
      await onSaved();
    } catch (saveError) {
      setError(errorMessage(saveError, t("The profile was not saved.")));
    } finally {
      setSaving(false);
    }
  };

  const fields: Array<[keyof UpdateAdminProviderInput, string]> = [
    ["business_name", "Business name"],
    ["contact_name", "Owner's name"],
    ["city", "City"],
    ["contact_phone", "Phone"],
  ];
  return (
    <Section title={t("Edit profile")}>
      <form className="console-form" onSubmit={(event) => void submit(event)}>
        {fields.map(([key, label]) => (
          <label className="ops-field" key={key}>
            <span className="ops-label">{t(label)}</span>
            <input className="ops-input" onChange={(event) => setForm((current) => ({ ...current, [key]: event.target.value }))} value={String(form[key] ?? "")} />
          </label>
        ))}
        <label className="ops-field console-form-wide">
          <span className="ops-label">{t("Internal application note")}</span>
          <textarea className="ops-textarea" onChange={(event) => setForm((current) => ({ ...current, application_note: event.target.value }))} rows={3} value={form.application_note ?? ""} />
        </label>
        <p className="console-muted console-form-wide">{t("The owner's email can't be changed here.")}</p>
        {error ? <p className="console-error console-form-wide" role="alert">{error}</p> : null}
        <div className="console-button-row console-form-wide">
          <button className="ops-button" data-variant="primary" disabled={saving} type="submit">{saving ? t("Saving...") : t("Save profile")}</button>
        </div>
      </form>
    </Section>
  );
}
